import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { opportunityApi } from '../services/api';
import { OpportunitySummary, OpportunityFilters } from '../types';
import { TYPE_LABELS, REWARD_LABELS, RISK_LABELS, DIFFICULTY_LABELS, RISK_METHODOLOGY } from '../data/opportunities';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const selectClass =
  'border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-blue-500';

export const OportunidadesPage: React.FC = () => {
  const [opportunities, setOpportunities] = useState<OpportunitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<OpportunityFilters>({});

  useEffect(() => {
    setLoading(true);
    opportunityApi
      .getOpportunities(filters)
      .then(setOpportunities)
      .catch(() => setOpportunities([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key: keyof OpportunityFilters, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value) next[key] = value as any;
      else delete next[key];
      return next;
    });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Radar de Oportunidades Cripto | ${SITE_NAME}`,
    url: `${SITE_URL}/oportunidades`,
    inLanguage: 'pt-BR',
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>Radar de Oportunidades Cripto: Airdrops, Learn & Earn e Testnets | {SITE_NAME}</title>
        <meta
          name="description"
          content="Oportunidades verificadas do mercado cripto: airdrops, learn & earn, quests, testnets e faucets — com nível de risco, requisitos, prazo, fonte oficial e data de verificação editorial."
        />
        <link rel="canonical" href={`${SITE_URL}/oportunidades`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-5xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <span className="text-gray-700">Oportunidades</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Radar de Oportunidades</h1>
          <p className="text-gray-600 leading-relaxed mb-6 max-w-3xl">
            Airdrops, programas learn & earn, quests, testnets e faucets — cada oportunidade listada aqui
            passa por revisão editorial: verificamos a fonte oficial, classificamos o risco, listamos
            requisitos e registramos a data da última verificação. <strong>Nenhuma recompensa estimada é
            garantida</strong> e nada nesta página é recomendação de investimento.
          </p>

          {/* Filtros */}
          <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-3 items-center">
            <select value={filters.type || ''} onChange={(e) => setFilter('type', e.target.value)} className={selectClass} aria-label="Filtrar por tipo">
              <option value="">Todos os tipos</option>
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select value={filters.risk || ''} onChange={(e) => setFilter('risk', e.target.value)} className={selectClass} aria-label="Filtrar por risco">
              <option value="">Qualquer risco</option>
              <option value="baixo">Risco baixo</option>
              <option value="moderado">Risco moderado</option>
              <option value="alto">Risco alto</option>
            </select>
            <select value={filters.needsWallet || ''} onChange={(e) => setFilter('needsWallet', e.target.value)} className={selectClass} aria-label="Filtrar por carteira">
              <option value="">Carteira: tanto faz</option>
              <option value="false">Sem carteira</option>
              <option value="true">Exige carteira</option>
            </select>
            <select value={filters.needsKyc || ''} onChange={(e) => setFilter('needsKyc', e.target.value)} className={selectClass} aria-label="Filtrar por KYC">
              <option value="">KYC: tanto faz</option>
              <option value="false">Sem KYC</option>
              <option value="true">Exige KYC</option>
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.freeOnly === 'true'}
                onChange={(e) => setFilter('freeOnly', e.target.checked ? 'true' : '')}
                className="rounded"
              />
              Sem custo obrigatório
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.hasDeadline === 'true'}
                onChange={(e) => setFilter('hasDeadline', e.target.checked ? 'true' : '')}
                className="rounded"
              />
              Com prazo definido
            </label>
          </div>

          {/* Lista */}
          {loading ? (
            <div className="text-gray-500 text-center py-10">Carregando oportunidades...</div>
          ) : opportunities.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-600 mb-8">
              <p className="font-semibold text-gray-800 mb-1">Nenhuma oportunidade ativa com esses filtros.</p>
              <p className="text-sm">
                Novas oportunidades são adicionadas após verificação editorial. Enquanto isso, veja abaixo
                como avaliamos cada uma — e como se proteger de golpes.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {opportunities.map((op) => (
                <Link
                  key={op.id}
                  to={`/oportunidades/${op.slug}`}
                  className="bg-white rounded-xl shadow p-5 hover:shadow-lg transition flex flex-col"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full text-xs font-bold">
                      {TYPE_LABELS[op.type]}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${RISK_LABELS[op.riskLevel].className}`}>
                      {RISK_LABELS[op.riskLevel].label}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${REWARD_LABELS[op.rewardClass].className}`}>
                      {REWARD_LABELS[op.rewardClass].label}
                    </span>
                  </div>
                  <h2 className="font-bold text-gray-900 mb-1">{op.name}</h2>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-3 flex-grow">{op.description}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>📁 {op.project}</span>
                    {op.blockchain && <span>⛓️ {op.blockchain}</span>}
                    {op.estimatedTime && <span>⏱️ {op.estimatedTime}</span>}
                    <span>🎓 {DIFFICULTY_LABELS[op.difficulty]}</span>
                    {op.deadline && (
                      <span className="text-amber-700 font-semibold">
                        ⏳ até {format(new Date(op.deadline), 'dd/MM/yyyy')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Verificado em {format(new Date(op.lastVerifiedAt), 'dd/MM/yyyy', { locale: ptBR })}
                  </p>
                </Link>
              ))}
            </div>
          )}

          {/* Metodologia de risco — conteúdo indexável permanente */}
          <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Como classificamos o risco</h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              Toda oportunidade recebe uma classificação de risco — <strong>baixo</strong>,{' '}
              <strong>moderado</strong>, <strong>alto</strong> ou <strong>não recomendado</strong> — com base
              nos seguintes critérios:
            </p>
            <ul className="space-y-2">
              {RISK_METHODOLOGY.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-700 text-[15px] leading-relaxed">
                  <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-blue-500" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 text-[15px] leading-relaxed mt-4">
              Oportunidades classificadas como "não recomendado" só aparecem quando o alerta em si tem valor
              informativo — sempre com a justificativa explícita.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Classificações de recompensa</h2>
            <ul className="space-y-3 text-[15px] text-gray-700 leading-relaxed">
              <li><strong>Recompensa confirmada</strong> — valor ou ativo anunciado oficialmente pelo projeto.</li>
              <li><strong>Recompensa estimada</strong> — baseada em campanhas anteriores ou expectativa de mercado. <em>Nunca é garantida.</em></li>
              <li><strong>Somente pontos</strong> — o programa distribui pontos sem valor monetário definido.</li>
              <li><strong>Recompensa não divulgada</strong> — o projeto não anunciou o que será distribuído, nem se será.</li>
              <li><strong>Elegibilidade futura possível</strong> — a atividade pode contar para um airdrop futuro não confirmado.</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Segurança em primeiro lugar</h2>
            <ul className="space-y-2 text-[15px] text-gray-700 leading-relaxed list-disc pl-5">
              <li><strong>Nunca</strong> informe sua seed phrase ou chave privada — nenhuma oportunidade legítima pede isso.</li>
              <li>Use uma carteira separada, com poucos fundos, para interagir com campanhas e testnets.</li>
              <li>Confira sempre a URL oficial (listada em cada página) antes de conectar a carteira.</li>
              <li>Desconfie de prazos urgentes, promessas de lucro garantido e mensagens diretas não solicitadas.</li>
              <li>Revogue aprovações de contratos que você não usa mais.</li>
            </ul>
          </section>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
            <strong>⚠️ Aviso:</strong> as oportunidades listadas são informativas e passam por verificação
            editorial na data indicada, mas condições mudam rapidamente e golpistas imitam campanhas
            legítimas. Verifique sempre a fonte oficial antes de participar. Nada aqui é recomendação de
            investimento ou garantia de recompensa.
          </div>
        </div>
      </div>
    </>
  );
};
